/**
 * The three boxes under a room — the person, the log, the agent — each collapsible (owner, 08/10/2026): every box
 * has two controls. One folds only that box. The other, "general", folds all three at once, and from the same
 * place opens all three again. Remembered per person, in this browser.
 */

import { useCallback, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown } from 'lucide-react'
import { useI18n } from '../lib/i18n'

export const ROOM_BOXES = ['person', 'log', 'agent'] as const
export type RoomBoxId = (typeof ROOM_BOXES)[number]

const KEY = 'devpulse:room-boxes'

function read(): Set<RoomBoxId> {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown
    return new Set(Array.isArray(value) ? value.filter((v): v is RoomBoxId => ROOM_BOXES.includes(v as RoomBoxId)) : [])
  } catch {
    return new Set()
  }
}

function write(closed: Set<RoomBoxId>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...closed]))
  } catch {
    // Private storage: the boxes open again next time.
  }
}

/** Which boxes are folded, and the two ways to change it. */
export function useRoomBoxes() {
  const [closed, setClosed] = useState<Set<RoomBoxId>>(read)
  const update = (next: Set<RoomBoxId>) => {
    setClosed(next)
    write(next)
  }
  const allClosed = ROOM_BOXES.every((id) => closed.has(id))
  const toggle = useCallback((id: RoomBoxId) => {
    const next = new Set(closed)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    update(next)
  }, [closed])
  // "General": all three folded, or — when they all are — all three open again.
  const toggleAll = useCallback(() => update(allClosed ? new Set() : new Set(ROOM_BOXES)), [allClosed])
  return { closed, allClosed, toggle, toggleAll }
}

export function RoomBox({
  id,
  title,
  boxes,
  children,
}: {
  id: RoomBoxId
  title: string
  boxes: ReturnType<typeof useRoomBoxes>
  children: ReactNode
}) {
  const { t } = useI18n()
  const folded = boxes.closed.has(id)
  return (
    <div className={`room-box${folded ? ' is-folded' : ''}`} data-room-box={id}>
      <div className="room-box__head">
        <span className="room-box__title">{title}</span>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm"
          onClick={boxes.toggleAll}
          aria-label={boxes.allClosed ? t('room.boxes.openAll') : t('room.boxes.foldAll')}
          title={boxes.allClosed ? t('room.boxes.openAll') : t('room.boxes.foldAll')}
          data-room-box-all
        >
          {boxes.allClosed ? <ChevronsUpDown size={13} /> : <ChevronsDownUp size={13} />}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm"
          onClick={() => boxes.toggle(id)}
          aria-expanded={!folded}
          aria-label={folded ? t('room.boxes.open', { name: title }) : t('room.boxes.fold', { name: title })}
          title={folded ? t('room.boxes.open', { name: title }) : t('room.boxes.fold', { name: title })}
          data-room-box-one
        >
          {folded ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </button>
      </div>
      {!folded && <div className="room-box__body">{children}</div>}
    </div>
  )
}
